import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import GuapLogo from './GuapLogo';

const Navbar = () => {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  const navLinks = [
    { to: '/', label: t('nav_home') },
    { to: '/program', label: t('nav_program') },
    { to: '/project-map', label: t('nav_map') },
    { to: '/gosts', label: t('nav_gosts') },
    { to: '/tasks', label: t('nav_tasks') },
    { to: '/contacts', label: t('nav_contacts') },
  ];

  return (
    <nav
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        transition: 'all 0.3s ease',
        background: scrolled
          ? 'rgba(10, 10, 20, 0.95)'
          : 'rgba(10, 10, 20, 0.5)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderBottom: scrolled ? '1px solid rgba(255,255,255,0.08)' : '1px solid transparent',
      }}
    >
      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '72px' }}>
          {/* Logo */}
          <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <GuapLogo size={40} />
            <div>
              <div style={{
                fontSize: '15px',
                fontWeight: 700,
                color: 'rgba(255,255,255,0.95)',
                letterSpacing: '-0.3px',
                lineHeight: 1.2,
              }}>
                ГУАП
                <span style={{
                  background: 'linear-gradient(135deg, #2E5AFF, #FF3B9A)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  marginLeft: '6px',
                }}>| Проектное управление</span>
              </div>
              <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', letterSpacing: '2px' }}>
                {t('nav_slogan').toUpperCase()}
              </div>
            </div>
          </Link>

          {/* Desktop nav */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }} className="hide-mobile">
            {navLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                className="nav-link"
                style={{
                  textDecoration: 'none',
                  padding: '8px 14px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: location.pathname === link.to
                    ? 'rgba(255,255,255,0.95)'
                    : 'rgba(255,255,255,0.65)',
                  background: location.pathname === link.to
                    ? 'rgba(46,90,255,0.15)'
                    : 'transparent',
                  transition: 'all 0.2s ease',
                  position: 'relative',
                }}
              >
                {link.label}
                {location.pathname === link.to && (
                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: '14px',
                    right: '14px',
                    height: '2px',
                    background: 'linear-gradient(135deg, #2E5AFF, #FF3B9A)',
                    borderRadius: '2px',
                  }} />
                )}
              </Link>
            ))}

            {/* Lang switcher */}
            <button
              onClick={() => setLang(lang === 'ru' ? 'en' : 'ru')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 14px',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: '8px',
                background: 'transparent',
                color: 'rgba(255,255,255,0.7)',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
                marginLeft: '8px',
                transition: 'all 0.2s ease',
              }}
              onMouseOver={e => {
                e.currentTarget.style.borderColor = '#2E5AFF';
                e.currentTarget.style.color = '#fff';
              }}
              onMouseOut={e => {
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)';
                e.currentTarget.style.color = 'rgba(255,255,255,0.7)';
              }}
            >
              <Globe size={14} />
              {lang === 'ru' ? 'EN' : 'RU'}
            </button>
          </div>

          {/* Mobile burger */}
          <button
            className="hide-desktop"
            onClick={() => setMenuOpen(!menuOpen)}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'rgba(255,255,255,0.8)',
              cursor: 'pointer',
              padding: '8px',
            }}
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div
            className="hide-desktop"
            style={{
              padding: '16px 0 24px',
              borderTop: '1px solid rgba(255,255,255,0.08)',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
            }}
          >
            {navLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                style={{
                  textDecoration: 'none',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: 500,
                  color: location.pathname === link.to ? '#fff' : 'rgba(255,255,255,0.65)',
                  background: location.pathname === link.to ? 'rgba(46,90,255,0.15)' : 'transparent',
                }}
              >
                {link.label}
              </Link>
            ))}
            <button
              onClick={() => setLang(lang === 'ru' ? 'en' : 'ru')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 16px',
                border: 'none',
                borderRadius: '8px',
                background: 'transparent',
                color: 'rgba(255,255,255,0.65)',
                fontSize: '15px',
                fontWeight: 500,
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <Globe size={16} />
              {lang === 'ru' ? 'English' : 'Русский'}
            </button>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
